import moment, { locale } from 'moment';
// moment-timezone bundles its own moment copy here, so it is kept to the timezone maths below:
// taking `moment`/`locale` from it would register formatRelativeTime's locale on that copy while
// every caller hands us a moment built by the one above, silently dropping the short-form labels.
import momentTz from 'moment-timezone';

/**
 * Timezone the business operates in. SLAs have to resolve to the same value for every viewer,
 * so business-day maths is always done here rather than in the viewer's local timezone.
 * Matches the server default (see functions-22/src/utils/date.ts).
 */
const BUSINESS_TIME_ZONE = 'America/New_York';

// Monday, used as the origin for the weekday arithmetic below.
const WEEKDAY_EPOCH = '2001-01-01';

const MINUTES_PER_DAY = 24 * 60;
const DAYS_PER_WEEK = 7;
const WEEKDAYS_PER_WEEK = 5;
const FRIDAY_ISO_WEEKDAY = 5;
const SATURDAY_ISO_WEEKDAY = 6;

const DateFormat = {
  DEFAULT_DATE_TIME_FORMAT: 'YYYY-MM-DDTHH:mm:ss',
  DATETIME_LOCAL_INPUT_FORMAT: 'YYYY-MM-DDTHH:mm',
  DEFAULT_DATE_FORMAT: 'YYYY-MM-DD',
  DEFAULT_DATE_WITH_TIME_FORMAT: 'YYYY-MM-DD-HH-mm-ss',
  SHORT_DATE_FORMAT: 'MM-DD-YY',
  DATE_FORMAT_SLASH: 'MM/DD/YYYY',
  DATE_FORMAT_SLASH_WITH_TIME_FORMAT: 'MM/DD/YY HH:mm:ss',
  DATE_FORMAT_COMMA: 'MMMM DD, YYYY',
  DATE_FORMAT_COMMA_TH: 'MMMM Do, YYYY',
  DATE_FORMAT_TIME_MINUTES: 'YYYY-MM-DD HH:mm',
  LOCAL_DATE_AND_TIME: 'L LT',
  CONTRACT_DATE_FORMAT: 'MMMM D, YYYY',
  MONTH_NAME_DATE_FORMAT: 'DD MMMM YYYY',
  MONTH_NAME: 'MMMM',
  YEAR_MONTH_FORMAT: 'YYYY-MM',
  YEAR_MONTH_DISPLAY_FORMAT: 'MMMM YYYY',
  YEAR_MONTH_SHORT_DISPLAY_FORMAT: 'MMM YY',
  DATE_YEAR_MONTH_SHORT_DISPLAY_FORMAT: 'MMM DD YYYY',
  SHORT_MONTH_DAY_COMMA_YEAR_FORMAT: 'MMM DD, YYYY',
  // The same, with the day unpadded: 'Nov 9, 2026' rather than 'Nov 09, 2026'.
  SHORT_MONTH_DAY_COMMA_YEAR_UNPADDED_FORMAT: 'MMM D, YYYY',
  DATE_MONTH_SHORT_DISPLAY_FORMAT: 'MMM DD',
  DATE_MONTH_TIME_SHORT_DISPLAY_FORMAT: 'MMM DD, HH:mm',
  DATE_MONTH_YEAR_TIME_SHORT_DISPLAY_FORMAT: 'MMM DD, YYYY - HH:mm',
  DATE_MONTH_YEAR_TIME_12H_FORMAT: 'MMM D, YYYY h:mm A',
  DATE_FORMAT_YYYYMMDD: 'YYYYMMDD',
  DATE_FORMAT_WEEKDAY_MONTH_FORMAT: 'ddd - D MMM',
  WEEK_DAY_NAME: 'dddd',
  TEXT_DATE_FORMAT: 'LL',
  ABBREVIATED_TEXT_DATE_FORMAT: 'll',
  CHAT_TIMESTAMP_FORMAT: 'HH:mm',
  DATE_ONLY_FORMAT: 'ddd DD',
  FULL_WEEKDAY_MONTH_FORMAT: 'dddd, MMMM DD, YYYY',
  FRIENDLY_DATETIME_WITH_TZ: 'ddd, MMM D [at] h:mm A z',
  SHORT_MONTH_DAY_TIME: 'MMM D, h:mm A',
  WEEKDAY_SHORT_MONTH_DAY: 'ddd, MMM D',
  TIME_PERIOD_FORMAT: 'h:mm A',
  TIME_PERIOD_TZ_FORMAT: 'h:mm A z',
  MONTH_DAY_FORMAT: 'MMMM D',
};

const isYesterday = (momentDate) => {
  const yesterday = moment().subtract(1, 'days').startOf('day');
  return momentDate.isSame(yesterday, 'day');
};

const isPastDay = (momentDate) => {
  const startOfNow = moment().startOf('day');
  return momentDate.isBefore(startOfNow, 'day');
};

const isToday = (momentDate) => {
  const today = moment();
  return today.isSame(momentDate, 'day');
};

const isTomorrow = (momentDate) => {
  const tomorrow = moment().add(1, 'days');
  return tomorrow.isSame(momentDate, 'day');
};

/**
 * Whether a date falls on a later day than today.
 *
 * Compared by day rather than by instant: a date a user picked carries whatever time of day the
 * picker attached to it, so an instant comparison against the start of today reads today-at-2pm as
 * being in the future.
 *
 * Accepts a moment, a dayjs object or a date string. The dayjs case is why the comparison is driven
 * from the value rather than from `moment()`: MUI's date pickers run on the dayjs adapter, and
 * wrapping one of their values in `moment()` reads plain `year`/`month`/`day` keys while dayjs stores
 * `$y`/`$M`/`$D` — yielding a valid moment holding today instead of the chosen date, with nothing
 * raised. Both libraries accept the day unit and both compare correctly against a moment.
 *
 * @param {Object|String} date A moment, a dayjs object, or a date string.
 * @returns {Boolean} True when the date falls on a later day than today.
 */
const isFutureDay = (date) => {
  if (!date) {
    return false;
  }

  const now = moment();
  return typeof date.isAfter === 'function'
    ? date.isAfter(now, 'day')
    : moment(date).isAfter(now, 'day');
};

const toWeekdayDate = (momentDate) => (
  momentDate.format('ddd D')
);

const toWeekdayDateOfMonth = (momentDate) => (
  momentDate.format('ddd D MMM')
);

const getDayofWeek = () => (
  moment(new Date()).format('dddd')
);

const isSameMonth = (momentDate) => {
  const today = moment();
  return momentDate.isSame(today, 'month');
};

const isFirstDayOfMonth = (momentDate) => (
  parseInt(momentDate.format('D'), 10) === 1
);

const todayStartDate = () => (
  moment().startOf('day')
);

/**
 * Converts a UTC date to a local user date time.
 *
 * @param {Object} date Date to convert to local time date.
 * @returns {Object} A moment date instance.
 */
const convertToLocalDate = (date) => {
  const momentDate = moment(date);
  const localMomentDate = momentDate.subtract(momentDate.utcOffset(), 'minutes');

  return localMomentDate;
};

/**
 * Converts a date to a UTC date.
 *
 * @param {Object} date The date object to convert.
 * @returns {Object} A moment date instance.
 */
const convertToUTCDate = (date) => {
  const momentDate = moment(date);
  const offset = moment().utcOffset();
  momentDate.subtract(offset, 'minutes');

  return momentDate;
};

const nowUTCDate = () => convertToUTCDate(moment());

const nowInDateOnlyFormat = () => moment().format(DateFormat.DEFAULT_DATE_FORMAT);

const convertToLocalStartMomentDate = (date) => convertToLocalDate(date).startOf('day');

const calculateUTCDay = (date) => {
  const localTimeStartDate = moment(date).startOf('day');

  return {
    startDate: moment.utc(localTimeStartDate).startOf('day').toDate(),
    endDate: moment.utc(localTimeStartDate).endOf('day').toDate(),
  };
};

const timeStampToStr = (timestamp, format) => (timestamp
  ? moment(timestamp.toDate()).format(format)
  : '');

/**
 * Combines date and time from two different moment objects into a single one
 * @param {Object} momentDate a moment object representing the day
 * @param {Object} momentTime a moment object representing the time
 */
const combineDateAndTime = (momentDate, momentTime) => {
  // Clone first — startOf/add mutate the moment in place, and callers pass stateful
  // moments (e.g. a date-picker value) that must not be mutated as a side effect.
  const day = momentDate.clone().startOf('day');
  const timeDuration = moment.duration(momentTime.format('HH:mm'));
  return day.add(timeDuration);
};

/**
 * Converts a duration in seconds to a formatted string representing the equivalent in hours, minutes, and seconds.
 * @param {number} seconds - Duration in seconds
 * @returns {string} - Representing the equivalent duration in hours, minutes, and seconds
 */
const formatSeconds = (seconds) => {
  const duration = moment.duration(seconds, 'seconds');
  const formattedDurations = [];

  if (duration.hours() > 0) {
    formattedDurations.push(`${duration.hours()}hr`);
  }
  if (duration.minutes() > 0) {
    formattedDurations.push(`${duration.minutes()}mins`);
  }
  if (duration.seconds() > 0) {
    formattedDurations.push(`${duration.seconds()}s`);
  }

  return formattedDurations.join(' ');
};

/**
 * Calculates all the dates between a start and end date for specific days of the week.
 *
 * @param {string} startDate - The start date in 'YYYY-MM-DD' format.
 * @param {string} endDate - The end date in 'YYYY-MM-DD' format.
 * @param {number[]} days - An array of numbers representing the days of the week to include in the range
 *                          (0 = Sunday, 1 = Monday, ..., 6 = Saturday).
 * @returns {string[]} An array of dates in 'YYYY-MM-DD' format for the specified days of the week
 *                     between the start and end dates.
 */
const getDatesBetween = (startDate, endDate, days) => {
  const dates = [];
  let currentDate = moment(startDate);

  while (currentDate <= moment(endDate)) {
    if (days.includes(currentDate.format('ddd'))) {
      dates.push(currentDate.format('YYYY-MM-DD'));
    }
    currentDate = currentDate.add(1, 'days');
  }
  return dates;
};

const combineDateAndTimeWithUTCOffset = (momentDate, momentTime, utcOffset) => {
  const dateTime = moment({
    year: momentDate.year(),
    month: momentDate.month(),
    date: momentDate.date(),
    hour: momentTime.hour(),
    minute: momentTime.minute(),
  });
  return dateTime.utcOffset(utcOffset, true);
};

/**
 * Formats a relative time string for a given moment date.
 * @param {Object} momentDate - A moment date object.
 * @returns {string} - A relative time string.
 * @example
 * formatRelativeTime(moment('2026-01-14T12:00:00Z')) // '1 day ago -> 1d'
 * formatRelativeTime(moment('2026-01-14T12:00:00Z')) // '1 hour ago -> 1h'
 */
const formatRelativeTime = (momentDate) => (
  momentDate.fromNow(locale('en', {
    relativeTime: {
      future: 'in %s',
      past: '%s ago',
      s: '1s',
      ss: '%ds',
      m: '1m',
      mm: '%dm',
      h: '1h',
      hh: '%dh',
      d: '1d',
      dd: '%dd',
      w: '1w',
      ww: '%dw',
      M: '1mo',
      MM: '%dmo',
      y: '1y',
      yy: '%dy',
    },
  }))
);

/**
 * Number of weekdays (Mon-Fri) in the [WEEKDAY_EPOCH, WEEKDAY_EPOCH + dayOffset) range.
 * @param {number} dayOffset - Whole days after WEEKDAY_EPOCH (may be negative).
 * @returns {number} The weekday count.
 */
const countWeekdaysBefore = (dayOffset) => {
  const dayOfWeek = ((dayOffset % DAYS_PER_WEEK) + DAYS_PER_WEEK) % DAYS_PER_WEEK;

  return WEEKDAYS_PER_WEEK * Math.floor(dayOffset / DAYS_PER_WEEK) + Math.min(dayOfWeek, WEEKDAYS_PER_WEEK);
};

/**
 * Projects a date onto a "business minutes since WEEKDAY_EPOCH" timeline where weekend days do
 * not exist. Subtracting two of these values therefore yields elapsed business time.
 *
 * Weekends carry no SLA time, so a weekend date is snapped to the nearest business instant:
 * forward to the following Monday 00:00 for the start of a range, back to the end of the
 * previous Friday for the end of a range.
 *
 * @param {*} date - A moment, Date, or date-like value.
 * @param {boolean} isRangeStart - Whether the date is the start of the range being measured.
 * @returns {number} Business minutes since WEEKDAY_EPOCH.
 */
const toBusinessMinutes = (date, isRangeStart) => {
  const businessDate = momentTz(date).tz(BUSINESS_TIME_ZONE);
  const startOfDay = businessDate.clone().startOf('day');
  // Both moments are local midnights, so the fractional diff is a whole number of days ±1h (DST).
  const dayOffset = Math.round(startOfDay.diff(momentTz.tz(WEEKDAY_EPOCH, BUSINESS_TIME_ZONE), 'days', true));
  const isoWeekday = startOfDay.isoWeekday();

  if (isoWeekday > FRIDAY_ISO_WEEKDAY) {
    const snappedDayOffset = isRangeStart
      // Next Monday: Saturday is 2 days away, Sunday 1.
      ? dayOffset + (DAYS_PER_WEEK + 1 - isoWeekday)
      // End of the previous Friday, which is the same instant as the start of that Saturday.
      : dayOffset - (isoWeekday - SATURDAY_ISO_WEEKDAY);

    return countWeekdaysBefore(snappedDayOffset) * MINUTES_PER_DAY;
  }

  return countWeekdaysBefore(dayOffset) * MINUTES_PER_DAY + businessDate.diff(startOfDay, 'minutes', true);
};

/**
 * Business-day hours elapsed between two dates. Saturdays and Sundays in the business timezone
 * are skipped entirely, so 24 business-day hours is one full business day and 48 is two.
 *
 * Note this measures wall-clock time on business days rather than working hours, and public
 * holidays are not excluded.
 *
 * @param {*} from - Start of the range (moment, Date, or date-like value).
 * @param {*} [to] - End of the range, defaults to now.
 * @returns {number} Elapsed business-day hours, never negative.
 * @example
 * // Friday 14:00 -> Monday 10:00 is 20 business-day hours.
 * getElapsedBusinessHours(friday2pm, monday10am) // 20
 */
const getElapsedBusinessHours = (from, to = moment()) => {
  const elapsedMinutes = toBusinessMinutes(to, false) - toBusinessMinutes(from, true);

  return Math.max(elapsedMinutes, 0) / 60;
};

/**
 * Normalises a value that may be a Firestore Timestamp, a Date, or a date-like value
 * into a JS Date. Feed activities expose raw Firestore Timestamps, while model getters
 * return Dates.
 * @param {*} value - A Firestore Timestamp, Date, or date-like value.
 * @returns {Date|null} The JS Date, or null when value is falsy.
 */
const toJsDate = (value) => {
  if (!value) return null;
  if (typeof value.toDate === 'function') return value.toDate();
  if (value instanceof Date) return value;
  const date = new Date(value);
  // An unparseable string gives an Invalid Date, which reads as a date everywhere until it renders.
  return Number.isNaN(date.getTime()) ? null : date;
};

export {
  isYesterday,
  isPastDay,
  isToday,
  isTomorrow,
  isFutureDay,
  isSameMonth,
  isFirstDayOfMonth,
  toWeekdayDate,
  toWeekdayDateOfMonth,
  getDayofWeek,
  convertToLocalDate,
  todayStartDate,
  convertToUTCDate,
  nowUTCDate,
  nowInDateOnlyFormat,
  convertToLocalStartMomentDate,
  calculateUTCDay,
  combineDateAndTime,
  DateFormat,
  timeStampToStr,
  formatSeconds,
  getDatesBetween,
  combineDateAndTimeWithUTCOffset,
  formatRelativeTime,
  toJsDate,
  getElapsedBusinessHours,
};
